import { SharePointFolder } from '../types';

export function findNodeByPath(
  nodes: SharePointFolder[],
  path: string
): SharePointFolder | null {
  for (const n of nodes) {
    if (n.path === path) return n;
    if (n.children?.length) {
      const hit = findNodeByPath(n.children, path);
      if (hit) return hit;
    }
  }
  return null;
}

export function ancestorPaths(path: string): string[] {
  const parts = path.split('/').filter(Boolean);
  return parts.map((_, i) => parts.slice(0, i + 1).join('/'));
}

const sortNodes = (nodes: SharePointFolder[]): SharePointFolder[] =>
  [...nodes].sort((a, b) =>
    b.name.localeCompare(a.name, undefined, { numeric: true })
  );

/**
 * Return a new tree with `newNode` inserted as a child of `parentPath`.
 * Pass `parentPath = ''` to insert at the root level.
 */
export function insertChildAtPath(
  nodes: SharePointFolder[],
  parentPath: string,
  newNode: SharePointFolder
): SharePointFolder[] {
  if (parentPath === '') {
    return sortNodes([newNode, ...nodes]);
  }
  return nodes.map(n => {
    if (n.path === parentPath) {
      const children = sortNodes([...(n.children || []), newNode]);
      return { ...n, children };
    }
    if (n.children?.length) {
      return { ...n, children: insertChildAtPath(n.children, parentPath, newNode) };
    }
    return n;
  });
}

/**
 * Return a new tree with the node at `path` (and all its descendants) removed.
 */
export function removeNodeAtPath(
  nodes: SharePointFolder[],
  path: string
): SharePointFolder[] {
  return nodes
    .filter(n => n.path !== path)
    .map(n =>
      n.children?.length
        ? { ...n, children: removeNodeAtPath(n.children, path) }
        : n
    );
}

/**
 * Recursively rewrite paths after a rename: every descendant path that started
 * with `oldPath/` gets its prefix swapped to `newPath/`.
 */
function rewriteSubtreePaths(
  nodes: SharePointFolder[],
  oldPrefix: string,
  newPrefix: string
): SharePointFolder[] {
  return nodes.map(n => ({
    ...n,
    path: n.path === oldPrefix
      ? newPrefix
      : n.path.startsWith(`${oldPrefix}/`)
        ? `${newPrefix}${n.path.slice(oldPrefix.length)}`
        : n.path,
    children: n.children?.length
      ? rewriteSubtreePaths(n.children, oldPrefix, newPrefix)
      : n.children,
  }));
}

/**
 * Return a new tree with the node at `sourcePath` relocated as a child of
 * `newParentPath` (empty string for the drive root). The moved node keeps its
 * original name; its path becomes `${newParentPath}/${name}` (or just `name`
 * at the root) and all descendants get their paths rewritten accordingly.
 */
export function moveNodeToParent(
  nodes: SharePointFolder[],
  sourcePath: string,
  newParentPath: string,
  updated: { path: string; webUrl?: string }
): SharePointFolder[] {
  const source = findNodeByPath(nodes, sourcePath);
  if (!source) return nodes;
  const moved: SharePointFolder = {
    ...source,
    path: updated.path,
    webUrl: updated.webUrl ?? source.webUrl,
    children: source.children?.length
      ? rewriteSubtreePaths(source.children, sourcePath, updated.path)
      : source.children,
  };
  const without = removeNodeAtPath(nodes, sourcePath);
  return insertChildAtPath(without, newParentPath, moved);
}

/**
 * Return a new tree with the node at `oldPath` renamed. Updates the node's
 * name/path/webUrl and rewrites all descendant paths to reflect the new prefix.
 */
export function renameNodeAtPath(
  nodes: SharePointFolder[],
  oldPath: string,
  updated: { name: string; path: string; webUrl?: string }
): SharePointFolder[] {
  return nodes.map(n => {
    if (n.path === oldPath) {
      const renamed: SharePointFolder = {
        ...n,
        name: updated.name,
        path: updated.path,
        webUrl: updated.webUrl,
      };
      if (renamed.children?.length) {
        renamed.children = rewriteSubtreePaths(renamed.children, oldPath, updated.path);
      }
      return renamed;
    }
    if (n.children?.length) {
      return { ...n, children: renameNodeAtPath(n.children, oldPath, updated) };
    }
    return n;
  });
}
